import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  dayMonthYear,
  get,
  italic,
  type LegalValues,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * UK cases, Acts, statutory instruments and Hansard in OSCOLA, footnote form.
 *
 * Sources checked (October 2026), both from the Oxford Faculty of Law, which publishes
 * them free of charge at law.ox.ac.uk/oscola:
 * - OSCOLA 4th edition (Oxford, 2012): §1.1.1 and §2.1 (cases: "case name | [year] |
 *   court | number, | [year] | volume | report abbreviation | first page"; neutral
 *   citations before reports, separated by a comma; the court in brackets after a report
 *   that has no neutral citation; paragraph pinpoints in square brackets; page pinpoints
 *   bare; the judge in brackets after the pinpoint), §1.2 (subsequent citations: "Phipps
 *   (n 31) 124", and ibid for the immediately preceding footnote), §1.3.2 (ranges: "as few
 *   figures as possible, but always at least two for the final number"), §2.4 (Acts: "Human
 *   Rights Act 1998, s 15(1)(b)"; regnal year and chapter for older Acts; short forms),
 *   §2.5 (statutory instruments: "Eggs and Chicks (England) Regulations 2009, SI
 *   2009/2163, reg 7(2)") and §3.4.2 (Hansard: "HC Deb 3 February 1977, vol 389, cols
 *   973–76"; written answers take a W suffix in the Commons and a WA prefix in the Lords).
 * - OSCOLA 5th edition (Hart, March 2026; the Faculty's PDF is dated 17 December 2025):
 *   "medium neutral citation" in place of "neutral citation"; ibid is not used ("ibid
 *   should not be used", §1.2.1); a short form of an Act goes in single quotation marks
 *   ("('SARAH') s 1"); the court identifier for a case with no medium neutral citation
 *   reads HL, PC, CA, KBD, QBD, Ch D or Fam (the 4th edition's High Court identifiers
 *   were QB, Ch and F); a range never starts a final number with 0 (201–203) and
 *   paragraph ranges are never truncated ([120]–[123]).
 * - Neutral citation forms: appendix 4.1 (4th) / 5.1 (5th).
 *
 * The page examples are checked against these functions in
 * `src/lib/__tests__/legal-styles-oscola.test.ts`.
 */

export type OscolaEdition = "4" | "5";

export type OscolaKind = "case" | "act" | "si" | "hansard";

export const OSCOLA_FIELDS = {
  name: "name",
  neutral: "neutral",
  report: "report",
  court: "court",
  paragraphs: "paragraphs",
  pages: "pages",
  judge: "judge",
  shortName: "shortName",
  inText: "inText",
  noteNumber: "noteNumber",
  title: "title",
  regnal: "regnal",
  jurisdiction: "jurisdiction",
  parts: "parts",
  shortTitle: "shortTitle",
  siNumber: "siNumber",
  welsh: "welsh",
  written: "written",
  house: "house",
  date: "date",
  volume: "volume",
  columns: "columns",
} as const;

export interface OscolaLabels {
  oscola4: string;
  oscola5: string;
  footnote: string;
  subsequent: string;
  ibid: string;
  tableOfCases: string;
  tableOfLegislation: string;
  caseNote4: string;
  caseNote5: string;
  actNote4: string;
  actNote5: string;
  siNote4: string;
  siNote5: string;
  hansardNote4: string;
  hansardNote5: string;
}

/* ------------------------------------------------------------------ helpers */

const RE_DIGITS = /^\d+$/;
const RE_SPLIT_LIST = /\s*(?:,|;|\band\b|&)\s*/i;
const RE_RANGE = /^(\d+)\s*[-–—]\s*(\d+)$/;
const RE_BRACKETS = /[[\]]/g;
const RE_PARA_PREFIX = /^(?:paras?\.?|¶)\s*/i;
const RE_PAGE_PREFIX = /^(?:pp?\.?|pages?)\s*/i;
const RE_PARENS = /^\((.*)\)$/;
const RE_LEADING_ZERO = /^0/;

/**
 * OSCOLA §1.3.2: both figures between ten and twenty, otherwise as few figures as
 * possible but at least two for the final number — 1–6, 11–17, 22–32, 121–221, 1782–83,
 * 530–31, 973–76. The 5th edition adds that the final number never starts with 0
 * (201–203, not 201–03).
 */
export function oscolaRange(
  first: string,
  last: string,
  edition: OscolaEdition
): string {
  if (
    !(RE_DIGITS.test(first) && RE_DIGITS.test(last)) ||
    first.length < 2 ||
    first.length !== last.length ||
    Number(last) <= Number(first)
  ) {
    return `${first}–${last}`;
  }
  let shared = 0;
  while (shared < first.length && first[shared] === last[shared]) {
    shared += 1;
  }
  let keep = Math.min(Math.max(last.length - shared, 2), last.length);
  if (edition === "5") {
    while (keep < last.length && RE_LEADING_ZERO.test(last.slice(-keep))) {
      keep += 1;
    }
  }
  return `${first}–${last.slice(-keep)}`;
}

function listItems(input: string): string[] {
  return input
    .split(RE_SPLIT_LIST)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** "34, 39, 43-47" → "[34], [39], [43]–[47]". Paragraph ranges are never shortened. */
export function paragraphPinpoint(input: string): string {
  const text = input.replace(RE_BRACKETS, "");
  return listItems(text)
    .map((item) => {
      const cleaned = item.replace(RE_PARA_PREFIX, "");
      const range = RE_RANGE.exec(cleaned);
      return range ? `[${range[1]}]–[${range[2]}]` : `[${cleaned}]`;
    })
    .join(", ");
}

/** "580" or "530-531" → "530–31". Several pinpoints are separated by commas. */
export function pagePinpoint(input: string, edition: OscolaEdition): string {
  return listItems(input)
    .map((item) => {
      const cleaned = item.replace(RE_PAGE_PREFIX, "");
      const range = RE_RANGE.exec(cleaned);
      return range ? oscolaRange(range[1], range[2], edition) : cleaned;
    })
    .join(", ");
}

function unparen(text: string): string {
  const match = RE_PARENS.exec(text.trim());
  return match ? match[1].trim() : text.trim();
}

function shortForm(text: string, edition: OscolaEdition): string {
  return edition === "5" ? `(‘${text}’)` : `(${text})`;
}

function footnoteRef(values: LegalValues): string {
  const number = get(values, OSCOLA_FIELDS.noteNumber);
  return number ? `n ${number}` : "n x";
}

/* -------------------------------------------------------------------- cases */

const RE_JR_APPLICATION = /^R \((.+?)\) v /;
const RE_EX_PARTE = /,? ex p (.+)$/;
const RE_CROWN = /^R v /;
const RE_PARTIES = /^(.+?) (?:v|c) /;
const RE_DESCRIPTION = /^(Re [^(]+?)\s*\(/;

/**
 * The name later footnotes use: the first-named party (§2.1.2), the applicant in a
 * judicial review ("R (Roberts) v Parole Board" → "Roberts"; "R v Lord Chancellor, ex p
 * Witham" → "Witham"), and the case itself for "Re …" without its descriptions.
 */
export function shortCaseName(name: string): string {
  const trimmed = name.trim();
  const applicant = RE_JR_APPLICATION.exec(trimmed);
  if (applicant) {
    return applicant[1];
  }
  const exParte = RE_EX_PARTE.exec(trimmed);
  if (exParte) {
    return exParte[1];
  }
  if (RE_CROWN.test(trimmed)) {
    const rest = trimmed.replace(RE_CROWN, "");
    return RE_PARTIES.exec(rest)?.[1] ?? rest;
  }
  const parties = RE_PARTIES.exec(trimmed);
  if (parties) {
    return parties[1];
  }
  return RE_DESCRIPTION.exec(trimmed)?.[1] ?? trimmed;
}

interface CaseDetails {
  name: string;
  short: string;
  reference: string;
  pinpoint: string;
  judge: string;
  nameInText: boolean;
}

/** The citation after the name: neutral citation, report, court, as §2.1 sets them out. */
function caseReference(values: LegalValues): string {
  const F = OSCOLA_FIELDS;
  const neutral = get(values, F.neutral);
  const report = get(values, F.report);
  const court = unparen(get(values, F.court));
  const references = [neutral, report].filter(Boolean).join(", ");
  const identifier = neutral === "" && court !== "" ? ` (${court})` : "";
  return `${references}${identifier}`.trim();
}

/** Paragraphs in square brackets win; pages follow the court identifier, or a comma. */
function casePinpoint(values: LegalValues, edition: OscolaEdition): string {
  const F = OSCOLA_FIELDS;
  const paragraphs = paragraphPinpoint(get(values, F.paragraphs));
  if (paragraphs !== "") {
    return paragraphs;
  }
  return pagePinpoint(get(values, F.pages), edition);
}

function readCase(values: LegalValues, edition: OscolaEdition): CaseDetails {
  const F = OSCOLA_FIELDS;
  const name = get(values, F.name);
  return {
    name,
    short: get(values, F.shortName) || shortCaseName(name),
    reference: caseReference(values),
    pinpoint: casePinpoint(values, edition),
    judge: get(values, F.judge),
    nameInText: get(values, F.inText) === "true",
  };
}

function joinPinpoint(
  reference: string,
  pinpoint: string,
  judge: string,
  usesPages: boolean,
  hasIdentifier: boolean
): string {
  let out = reference;
  if (pinpoint !== "") {
    const separator = usesPages && !hasIdentifier && out !== "" ? ", " : " ";
    out = `${out}${separator}${pinpoint}`.trim();
  }
  return judge === "" ? out : `${out} (${judge})`.trim();
}

function caseFootnote(
  details: CaseDetails,
  usesPages: boolean,
  hasIdentifier: boolean
): CitationParts {
  const cited = joinPinpoint(
    details.reference,
    details.pinpoint,
    details.judge,
    usesPages,
    hasIdentifier
  );
  if (details.nameInText) {
    return [plain(`${cited}.`)];
  }
  return tidyParts([italic(details.name), plain(` ${cited}.`)]);
}

function caseSubsequent(
  details: CaseDetails,
  values: LegalValues
): CitationParts {
  const pinpoint = details.pinpoint ? ` ${details.pinpoint}` : "";
  const judge = details.judge ? ` (${details.judge})` : "";
  return [
    italic(details.short),
    plain(` (${footnoteRef(values)})${pinpoint}${judge}.`),
  ];
}

function caseIbid(details: CaseDetails): CitationParts {
  const pinpoint = details.pinpoint ? ` ${details.pinpoint}` : "";
  const judge = details.judge ? ` (${details.judge})` : "";
  return [plain(`ibid${pinpoint}${judge}.`)];
}

export function oscolaCaseIsReady(values: LegalValues): boolean {
  const F = OSCOLA_FIELDS;
  return (
    get(values, F.name) !== "" &&
    (get(values, F.neutral) !== "" ||
      get(values, F.report) !== "" ||
      get(values, F.court) !== "")
  );
}

function formatCase(
  edition: OscolaEdition,
  values: LegalValues,
  labels: OscolaLabels
): CitationOutput {
  const F = OSCOLA_FIELDS;
  const details = readCase(values, edition);
  const usesPages = get(values, F.paragraphs) === "" && details.pinpoint !== "";
  const hasIdentifier =
    get(values, F.neutral) === "" && get(values, F.court) !== "";
  const table: CitationParts = [
    plain(`${details.name} ${details.reference}`.trim()),
  ];
  const entries = [
    {
      label: labels.footnote,
      parts: caseFootnote(details, usesPages, hasIdentifier),
    },
    { label: labels.subsequent, parts: caseSubsequent(details, values) },
  ];
  if (edition === "4") {
    entries.push({ label: labels.ibid, parts: caseIbid(details) });
  }
  entries.push({ label: labels.tableOfCases, parts: table });
  return {
    key: "oscola",
    label: edition === "5" ? labels.oscola5 : labels.oscola4,
    entries,
    note: edition === "5" ? labels.caseNote5 : labels.caseNote4,
  };
}

/* ------------------------------------------------------------- legislation */

interface Legislation {
  title: string;
  qualifier: string;
  parts: string;
  short: string;
}

function readAct(values: LegalValues): Legislation {
  const F = OSCOLA_FIELDS;
  const regnal = get(values, F.regnal);
  const jurisdiction = unparen(get(values, F.jurisdiction));
  const qualifier = [regnal, jurisdiction]
    .filter(Boolean)
    .map((item) => ` (${item})`)
    .join("");
  return {
    title: get(values, F.title),
    qualifier,
    parts: get(values, F.parts),
    short: get(values, F.shortTitle),
  };
}

function legislationFirst(
  item: Legislation,
  edition: OscolaEdition
): CitationParts {
  if (item.short !== "") {
    const pinpoint = item.parts ? ` ${item.parts}` : "";
    return [
      plain(
        `${item.title}${item.qualifier} ${shortForm(item.short, edition)}${pinpoint}.`
      ),
    ];
  }
  const pinpoint = item.parts ? `, ${item.parts}` : "";
  return [plain(`${item.title}${item.qualifier}${pinpoint}.`)];
}

function legislationLater(item: Legislation): CitationParts {
  return [plain(`${item.short}${item.parts ? `, ${item.parts}` : ""}.`)];
}

export function oscolaActIsReady(values: LegalValues): boolean {
  return get(values, OSCOLA_FIELDS.title) !== "";
}

function formatAct(
  edition: OscolaEdition,
  values: LegalValues,
  labels: OscolaLabels
): CitationOutput {
  const item = readAct(values);
  const entries = [
    { label: labels.footnote, parts: legislationFirst(item, edition) },
  ];
  if (item.short !== "") {
    entries.push({ label: labels.subsequent, parts: legislationLater(item) });
  }
  entries.push({
    label: labels.tableOfLegislation,
    parts: [plain(`${item.title}${item.qualifier}`)],
  });
  return {
    key: "oscola",
    label: edition === "5" ? labels.oscola5 : labels.oscola4,
    entries,
    note: edition === "5" ? labels.actNote5 : labels.actNote4,
  };
}

const RE_SI_PREFIX = /^(SI|SSI|SR & O|SR)\s+/i;

/** "2009/2163" → "SI 2009/2163"; an SSI, SR or SR & O prefix is kept. */
export function siReference(input: string): string {
  const text = input.trim();
  const prefix = RE_SI_PREFIX.exec(text);
  if (prefix) {
    return `${prefix[1].toUpperCase()} ${text.replace(RE_SI_PREFIX, "")}`;
  }
  return `SI ${text}`;
}

export function oscolaSiIsReady(values: LegalValues): boolean {
  const F = OSCOLA_FIELDS;
  return get(values, F.title) !== "" && get(values, F.siNumber) !== "";
}

function readSi(values: LegalValues): Legislation & { number: string } {
  const F = OSCOLA_FIELDS;
  const welsh = unparen(get(values, F.welsh));
  const number = `${siReference(get(values, F.siNumber))}${welsh ? ` (${welsh})` : ""}`;
  return {
    title: get(values, F.title),
    qualifier: "",
    parts: get(values, F.parts),
    short: get(values, F.shortTitle),
    number,
  };
}

function siFirst(
  item: Legislation & { number: string },
  edition: OscolaEdition
): CitationParts {
  if (item.short !== "") {
    const pinpoint = item.parts ? ` ${item.parts}` : "";
    return [
      plain(
        `${item.title}, ${item.number} ${shortForm(item.short, edition)}${pinpoint}.`
      ),
    ];
  }
  const pinpoint = item.parts ? `, ${item.parts}` : "";
  return [plain(`${item.title}, ${item.number}${pinpoint}.`)];
}

function formatSi(
  edition: OscolaEdition,
  values: LegalValues,
  labels: OscolaLabels
): CitationOutput {
  const item = readSi(values);
  const entries = [{ label: labels.footnote, parts: siFirst(item, edition) }];
  if (item.short !== "") {
    entries.push({ label: labels.subsequent, parts: legislationLater(item) });
  }
  entries.push({
    label: labels.tableOfLegislation,
    parts: [plain(`${item.title}, ${item.number}`)],
  });
  return {
    key: "oscola",
    label: edition === "5" ? labels.oscola5 : labels.oscola4,
    entries,
    note: edition === "5" ? labels.siNote5 : labels.siNote4,
  };
}

/* ------------------------------------------------------------------ Hansard */

/**
 * Column references: "1501-1525" → "cols 1501–25"; one number → "col 893". A written
 * answer takes the W suffix in the Commons ("col 505W") and the WA prefix in the Lords
 * ("col WA261").
 */
export function hansardColumns(
  input: string,
  house: string,
  written: boolean,
  edition: OscolaEdition
): string {
  const items = listItems(input).map((item) => {
    const range = RE_RANGE.exec(item);
    return range ? oscolaRange(range[1], range[2], edition) : item;
  });
  if (items.length === 0) {
    return "";
  }
  const plural = items.length > 1 || items[0].includes("–");
  let text = items.join(", ");
  if (written) {
    text = house === "HL" ? `WA${text}` : `${text}W`;
  }
  return `${plural ? "cols" : "col"} ${text}`;
}

export function oscolaHansardIsReady(values: LegalValues): boolean {
  const F = OSCOLA_FIELDS;
  return (
    parsePartialDate(get(values, F.date)) !== null &&
    get(values, F.volume) !== "" &&
    get(values, F.columns) !== ""
  );
}

function formatHansard(
  edition: OscolaEdition,
  values: LegalValues,
  labels: OscolaLabels
): CitationOutput {
  const F = OSCOLA_FIELDS;
  const house = get(values, F.house) === "HL" ? "HL" : "HC";
  const date = dayMonthYear(parsePartialDate(get(values, F.date)));
  const columns = hansardColumns(
    get(values, F.columns),
    house,
    get(values, F.written) === "true",
    edition
  );
  const citation = `${house} Deb ${date}, vol ${get(values, F.volume)}, ${columns}.`;
  return {
    key: "oscola",
    label: edition === "5" ? labels.oscola5 : labels.oscola4,
    entries: [{ label: labels.footnote, parts: [plain(citation)] }],
    note: edition === "5" ? labels.hansardNote5 : labels.hansardNote4,
  };
}

/* ------------------------------------------------------------------- public */

export function oscolaIsReady(kind: OscolaKind, values: LegalValues): boolean {
  switch (kind) {
    case "case":
      return oscolaCaseIsReady(values);
    case "act":
      return oscolaActIsReady(values);
    case "si":
      return oscolaSiIsReady(values);
    default:
      return oscolaHansardIsReady(values);
  }
}

export function formatOscola(
  kind: OscolaKind,
  edition: OscolaEdition,
  values: LegalValues,
  labels: OscolaLabels
): CitationOutput[] {
  switch (kind) {
    case "case":
      return [formatCase(edition, values, labels)];
    case "act":
      return [formatAct(edition, values, labels)];
    case "si":
      return [formatSi(edition, values, labels)];
    default:
      return [formatHansard(edition, values, labels)];
  }
}
